import { ActionIcon, Button, Group, Popover, Tooltip } from "@mantine/core";
import { IconMoodSmile } from "@tabler/icons-react";
import React, { useMemo, useState } from "react";

const EMOJI_SET = ["\u{1F44D}", "\u2764\uFE0F", "\u{1F602}", "\u{1F62E}", "\u{1F622}", "\u{1F389}"];

interface EmojiReactionsProps {
  reactions: { user: { _id: string; name: string }; date: string; emoji: string }[];
  currentUserId: string;
  onReact: (emoji: string) => void;
}

interface GroupedReaction {
  emoji: string;
  count: number;
  userNames: string[];
  hasCurrentUser: boolean;
}

const EmojiReactions: React.FC<EmojiReactionsProps> = ({ reactions, currentUserId, onReact }) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<string, GroupedReaction>();
    for (const r of reactions) {
      const existing = map.get(r.emoji);
      if (existing) {
        existing.count++;
        existing.userNames.push(r.user.name);
        if (String(r.user._id) === String(currentUserId)) {
          existing.hasCurrentUser = true;
        }
      } else {
        map.set(r.emoji, {
          emoji: r.emoji,
          count: 1,
          userNames: [r.user.name],
          hasCurrentUser: String(r.user._id) === String(currentUserId),
        });
      }
    }
    return Array.from(map.values());
  }, [reactions, currentUserId]);

  const handlePickerSelect = (emoji: string) => {
    setPickerOpen(false);
    onReact(emoji);
  };

  return (
    <Group gap="xs" wrap="wrap" align="center">
      {grouped.map((g) => (
        <Tooltip key={g.emoji} label={g.userNames.join(", ")} withArrow>
          <Button
            size="compact-xs"
            variant={g.hasCurrentUser ? "light" : "default"}
            onClick={() => onReact(g.emoji)}
            styles={{
              root: {
                fontWeight: g.hasCurrentUser ? 600 : 400,
              },
            }}
          >
            {g.emoji} {g.count}
          </Button>
        </Tooltip>
      ))}
      <Popover opened={pickerOpen} onChange={setPickerOpen} position="top" withArrow>
        <Popover.Target>
          <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => setPickerOpen((o) => !o)}>
            <IconMoodSmile size={16} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown p="xs">
          <Group gap="xs">
            {EMOJI_SET.map((emoji) => (
              <ActionIcon key={emoji} variant="subtle" size="lg" onClick={() => handlePickerSelect(emoji)}>
                <span style={{ fontSize: "1.2rem" }}>{emoji}</span>
              </ActionIcon>
            ))}
          </Group>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
};

export default EmojiReactions;
